/* eslint-disable @typescript-eslint/no-unused-vars */
import * as fb from "../../firebase";
import {
  packageLabelsMatch,
  PKG_WIN,
} from "functions/src/Subscriptions/packageIds";
import {
  collection,
  collectionGroup,
  query,
  where,
  onSnapshot,
} from "firebase/firestore";
import {
  QuoteSubscriptionRequest,
  SubscriptionInfo,
  SubscriptionPackage,
  SubscriptionPromotion,
  SubscriptionUpgrade,
} from "functions/src/Subscriptions/SubscriptionsInterfaces";
import { UserSubscription } from "functions/src/Users/UsersInterfaces";
import {
  SET_PROMOTIONS_CONFIG,
  SET_SUBS_CONFIG,
  SET_USER_ACTIVE,
  SET_USER_CURRENT_PACKAGE,
  SET_USER_SUBS,
  SubscriptionsState,
  UNSET_PROMOTIONS_CONFIG,
  UNSET_SUBS_CONFIG,
  UNSET_USER_SUBS,
  SET_IS_USER_DISTRIBUTOR,
  SET_UPGRADES_CONFIG,
  UNSET_UPGRADES_CONFIG,
  SET_USER_PACKAGE_STATUS,
  UserPackageStatus,
} from "./SubscriptionsConsts";
import { ActionContext } from "vuex";
import dayjs from "dayjs";
import { _determineUserPackageAndStatus } from "./tsFunctions";
import { subscriptionFamilyOf } from "functions/src/Subscriptions/packageIds";

/**
 * `endDate` en milisegundos, venga como venga del backend (Timestamp
 * serializado, Date o número). Sólo se usa para ordenar dentro de una familia,
 * así que un documento sin fecha se hunde al fondo en vez de romper nada.
 */
function endMs(sub: UserSubscription): number {
  const end = sub?.endDate as unknown;
  if (end == undefined) return -Infinity;
  if (typeof end === "number") return end;
  if (end instanceof Date) return end.getTime();
  const ts = end as { seconds?: number; _seconds?: number };
  const seconds = ts.seconds ?? ts._seconds;
  return seconds != undefined ? seconds * 1000 : -Infinity;
}

const initialState: SubscriptionsState = {
  SubscriptionsConfiguration: [],
  UserSubscriptions: [],
  SubscriptionsPromotions: new Array<[string, SubscriptionPromotion]>(),
  SubscriptionsUpgrades: new Array<[string, SubscriptionUpgrade]>(),
  UserPackage: "none",
  // Se arranca en «cargando» y no en «listo» a propósito: mientras nadie haya
  // preguntado, lo que la aplicación sabe del paquete del usuario es NADA, y el
  // `"none"` de aquí arriba es sólo el hueco donde irá la respuesta.
  UserPackageStatus: "cargando",
  isUserActive: false,
  isUserDistributor: false,
};

const state = { ...initialState };

const snapshotListeners = {
  userSubscriptions: () => {
    return;
  },
  subscriptionConf: () => {
    return;
  },
  promotionsConf: () => {
    return;
  },
  upgradesConf: () => {
    return;
  },
};
const mutations = {
  SET_SUBS_CONFIG(state: SubscriptionsState, subscription: SubscriptionInfo) {
    const index = state.SubscriptionsConfiguration.findIndex((item: any) => {
      return item[0] == subscription.subscriptionId;
    });
    if (index >= 0) {
      state.SubscriptionsConfiguration[index] = [
        subscription.subscriptionId,
        subscription,
      ];
    } else {
      state.SubscriptionsConfiguration.push([
        subscription.subscriptionId,
        subscription,
      ]);
    }
  },
  SET_USER_SUBS(state: SubscriptionsState, subscription: UserSubscription) {
    const index = state.UserSubscriptions.findIndex((item: any) => {
      return item[0] == subscription.subscriptionId;
    });
    if (index >= 0) {
      state.UserSubscriptions[index] = [
        subscription.subscriptionId,
        subscription,
      ];
    } else {
      state.UserSubscriptions.push([subscription.subscriptionId, subscription]);
    }
  },
  SET_USER_CURRENT_PACKAGE(
    state: SubscriptionsState,
    userCurrentPackage: SubscriptionPackage
  ) {
    state.UserPackage = userCurrentPackage;
  },
  SET_USER_ACTIVE(state: SubscriptionsState, isActive: boolean) {
    state.isUserActive = isActive;
  },
  SET_USER_PACKAGE_STATUS(
    state: SubscriptionsState,
    status: UserPackageStatus
  ) {
    state.UserPackageStatus = status;
  },
  SET_IS_USER_DISTRIBUTOR(state: SubscriptionsState, isDistributor: boolean) {
    state.isUserDistributor = isDistributor;
  },
  UNSET_USER_SUBS(state: SubscriptionsState, subscriptionId: string) {
    state.UserSubscriptions = state.UserSubscriptions.filter((item) => {
      return item[0] != subscriptionId;
    });
  },
  SET_PROMOTIONS_CONFIG(
    state: SubscriptionsState,
    promotion: SubscriptionPromotion
  ) {
    const index = state.SubscriptionsPromotions.findIndex((item: any) => {
      return item[0] == promotion._id;
    });
    if (index >= 0) {
      state.SubscriptionsPromotions[index] = [promotion._id, promotion];
    } else {
      state.SubscriptionsPromotions.push([promotion._id, promotion]);
    }
  },
  SET_UPGRADES_CONFIG(state: SubscriptionsState, upgrade: SubscriptionUpgrade) {
    const index = state.SubscriptionsUpgrades.findIndex((item: any) => {
      return item[0] == upgrade._id;
    });
    if (index >= 0) {
      state.SubscriptionsUpgrades[index] = [upgrade._id, upgrade];
    } else {
      state.SubscriptionsUpgrades.push([upgrade._id, upgrade]);
    }
  },
  CLEAR_USER_SUBS(state: SubscriptionsState) {
    state.UserSubscriptions = [];
    state.UserPackage = "none";
    // Al vaciar se vuelve a «cargando», que es la verdad: se ha olvidado lo que
    // se sabía. Dejarlo en «listo» con el paquete a `none` le enseñaría la
    // tienda entera al siguiente que entre, antes de saber nada de él.
    state.UserPackageStatus = "cargando";
    state.isUserActive = false;
    state.isUserDistributor = false;
  },
  UNSET_SUBS_CONFIG(state: SubscriptionsState, subscriptionId: string) {
    state.SubscriptionsConfiguration = state.SubscriptionsConfiguration.filter(
      (item) => {
        return item[0] != subscriptionId;
      }
    );
  },
  UNSET_PROMOTIONS_CONFIG(state: SubscriptionsState, promotionId: string) {
    state.SubscriptionsPromotions = state.SubscriptionsPromotions.filter(
      (item) => {
        return item[0] != promotionId;
      }
    );
  },
  UNSET_UPGRADES_CONFIG(state: SubscriptionsState, upgradeId: string) {
    state.SubscriptionsUpgrades = state.SubscriptionsUpgrades.filter((item) => {
      return item[0] != upgradeId;
    });
  },
};

const actions = {
  /**
   * QUÉ PAQUETE TIENE EL USUARIO Y SI ESTÁ VIGENTE, preguntado al servidor.
   *
   * Se extrae a su propia acción por dos motivos: para que el listener de aquí
   * abajo la llame UNA vez por instantánea (antes la llamaba una vez por
   * documento cambiado: cuatro suscripciones eran cuatro llamadas idénticas
   * compitiendo entre ellas, y la última en llegar mandaba), y para que la
   * pantalla pueda REINTENTAR sin volver a abrir el listener — reabrirlo dejaría
   * el anterior escuchando, porque `snapshotListeners` sólo guarda uno.
   *
   * El `catch` NO es decoración: sin él la promesa se rechaza dentro de un
   * `forEach` y nadie la recoge, así que el fallo se veía sólo en la consola y
   * el estado se quedaba en «cargando» para siempre.
   */
  async refreshUserPackageStatus(
    context: ActionContext<SubscriptionsState, any>
  ) {
    const username = context.rootState.auth.userProfileData?._usernamePK;
    if (!username) return;
    try {
      const result = await _determineUserPackageAndStatus(username);
      context.commit(SET_USER_CURRENT_PACKAGE, result.currentPackage);
      context.commit(SET_USER_ACTIVE, result.isPackageActive);
      context.commit(SET_IS_USER_DISTRIBUTOR, result.isLicenseActive);
      context.commit(SET_USER_PACKAGE_STATUS, "listo");
    } catch (error) {
      console.error("No se pudo leer el estado del paquete del usuario", error);
      context.commit(SET_USER_PACKAGE_STATUS, "error");
    }
  },
  async doGetUserSubscriptions(
    context: ActionContext<SubscriptionsState, any>
  ) {
    const username = context.rootState.auth.userProfileData._usernamePK;
    const _userSubscriptions = collection(
      fb.firestore,
      "users",
      username,
      "subscriptions"
    );
    context.commit(SET_USER_CURRENT_PACKAGE, "none");
    context.commit(SET_USER_ACTIVE, false);
    context.commit(SET_IS_USER_DISTRIBUTOR, false);
    // «Cargando» hasta que conteste el servidor. Los tres valores de arriba son
    // el hueco vacío, no una respuesta: `none` + `false` es exactamente lo que
    // tiene alguien que no ha comprado nunca nada, y sin esta marca la pantalla
    // de Suscripciones no puede distinguir un caso del otro.
    context.commit(SET_USER_PACKAGE_STATUS, "cargando");
    //try {
    const listener = onSnapshot(
      _userSubscriptions,
      (querySnapshot) => {
        querySnapshot.docChanges().forEach((change) => {
          const doc = change.doc;
          if (change.type == "added" || change.type == "modified") {
            const startDate = doc.data().startDate.toDate();
            const endDate = doc.data().endDate.toDate();
            const lastPayDate = doc.data().lastPayDate.toDate();

            const subscription = {
              subscriptionId: doc.id,
              startDate: startDate,
              endDate: endDate,
              lastPayDate: lastPayDate,
              username: username,
            } as UserSubscription;
            if (
              subscription.endDate != undefined &&
              subscription.startDate != undefined
            ) {
              subscription.isActive = subscription.endDate > new Date();
            }
            const now = dayjs();
            // Se usa la variable local, que YA es un `Date` de verdad (sale del
            // `.toDate()` de arriba), y no `subscription.endDate`.
            //
            // Es el mismo valor, pero no la misma verdad para el compilador: el
            // `as UserSubscription` de unas líneas más arriba le dice a TypeScript
            // «confía en mí», y a partir de ahí lee el tipo DECLARADO —que admite
            // un `Timestamp` de Firestore, y `dayjs` no acepta uno—. O sea, el
            // error de tipos lo fabricaba el propio cast, no el dato.
            const end = dayjs(endDate);
            subscription.daysLeft = Math.floor(end.diff(now, "day", true));

            context.commit(SET_USER_SUBS, subscription);
          } else if (change.type == "removed") {
            context.commit(UNSET_USER_SUBS, doc.id);
          }
        });
        // FUERA DEL BUCLE, y una sola vez. Aquí dentro había un `await` a la
        // callable por CADA documento cambiado; con las cuatro suscripciones de un
        // socio normal eran cuatro llamadas iguales a la vez —cuatro arranques en
        // frío en el peor caso— y el resultado que mandaba era el de la que
        // llegara la última.
        //
        // Y va DESPUÉS del `forEach` también cuando no hay ningún cambio: una
        // primera instantánea VACÍA (el que no tiene ninguna suscripción) no
        // entraba nunca al bucle, así que nadie preguntaba nada y la pantalla se
        // habría quedado esperando para siempre.
        context.dispatch("refreshUserPackageStatus");
      },
      // El listener también puede fallar —sin permisos, sin red y sin caché— y
      // hasta ahora ese fallo era MUDO: `onSnapshot` sólo tenía la rama del éxito.
      // Importa más que antes, porque ahora la pantalla de Suscripciones espera a
      // saber qué tiene el usuario antes de pintar nada: sin esta rama, un fallo
      // aquí la dejaría con el esqueleto puesto para siempre en vez de contarlo.
      (error) => {
        console.error(
          "Falló la escucha de las suscripciones del usuario",
          error
        );
        context.commit(SET_USER_PACKAGE_STATUS, "error");
      }
    );
    snapshotListeners.userSubscriptions = listener;
    //} catch (error) {
    //  return await Promise.reject(error);
    //}
  },
  async doGetSubscriptionsConfig(
    context: ActionContext<SubscriptionsState, any>
  ) {
    const _subscriptions = collection(fb.firestore, "subscriptions");
    const listener = onSnapshot(_subscriptions, (querySnapshot) => {
      querySnapshot.docChanges().forEach((change) => {
        const doc = change.doc;

        if (change.type == "added" || change.type == "modified") {
          const subscription = doc.data();
          subscription.subscriptionId = doc.id;
          context.commit(SET_SUBS_CONFIG, subscription);
        } else if (change.type == "removed") {
          context.commit(UNSET_SUBS_CONFIG, doc.id);
        }
      });
    });
    const _promotions = query(
      collectionGroup(fb.firestore, "promotions"),
      where("status", "==", "active"),
      where("_type", "==", "subscription")
    );
    const promotionsListener = onSnapshot(_promotions, (querySnapshot) => {
      querySnapshot.docChanges().forEach((change) => {
        const doc = change.doc;
        const promotion = doc.data() as SubscriptionPromotion;

        if (change.type == "added" || change.type == "modified") {
          context.commit(SET_PROMOTIONS_CONFIG, promotion);
        } else if (change.type == "removed") {
          context.commit(UNSET_PROMOTIONS_CONFIG, promotion._id);
        }
      });
    });
    const _upgrades = query(
      collectionGroup(fb.firestore, "subscription-upgrades"),
      where("status", "==", "active"),
      where("_type", "==", "upgrade")
    );
    const upgradesListener = onSnapshot(_upgrades, (querySnapshot) => {
      querySnapshot.docChanges().forEach((change) => {
        const doc = change.doc;
        const upgrade = doc.data() as SubscriptionUpgrade;

        if (change.type == "added" || change.type == "modified") {
          context.commit(SET_UPGRADES_CONFIG, upgrade);
        } else if (change.type == "removed") {
          context.commit(UNSET_UPGRADES_CONFIG, upgrade._id);
        }
      });
    });
    snapshotListeners.subscriptionConf = listener;
    snapshotListeners.promotionsConf = promotionsListener;
    snapshotListeners.upgradesConf = upgradesListener;
  },
  async quoteSubscription(
    context: ActionContext<SubscriptionsState, any>,
    request: QuoteSubscriptionRequest
  ) {
    const quoteSubs = fb.func.httpsCallable("Payments-QuoteSubscription");
    return quoteSubs(request)
      .then((r) => {
        if (r.data.OPCODE == "ERROR") {
          return r.data;
        } else {
          // remove 5 minutes from expiration time
          r.data.expirationTime = new Date(
            (r.data.expirationTime as any)._seconds * 1000
          );
          r.data.createdDate = new Date(
            (r.data.createdDate as any)._seconds * 1000
          );
          r.data.updatedDate = new Date(
            (r.data.updatedDate as any)._seconds * 1000
          );
          return r.data;
        }
      })
      .catch((error) => {
        console.error("Error: ", error);
        throw new Error(error);
      });
  },
  unsubscribeUserSubsListener() {
    snapshotListeners.userSubscriptions();
  },
  unsubscribeSubscriptionsConfListener() {
    snapshotListeners.subscriptionConf();
    snapshotListeners.promotionsConf();
    snapshotListeners.upgradesConf();
  },
};

const getters = {
  getUserSubscriptions(
    state: SubscriptionsState
  ): Array<[string, UserSubscription]> {
    return state.UserSubscriptions;
  },

  /**
   * El catálogo VENDIBLE: los paquetes que hoy se pueden ofrecer.
   *
   * Filtra los retirados, y el sitio del filtro está elegido. NO va en la
   * consulta de `subscriptions` (línea ~318) porque ahí se cargaría a medias el
   * catálogo para todo el mundo, y `getSubscriptionConfig` —el singular, el que
   * busca por id— dejaría de encontrar los retirados. Ése tiene que seguir
   * viéndolos: es quien pone el nombre a un `trading-academy` o un `gtr-vip` que
   * alguien todavía tiene contratado. Aquí el alcance es exactamente uno:
   * `CreateQuote.vue`, la ficha de pago de la consola de administración.
   *
   * ⚠️ EL CAMPO ES `active`, BOOLEANO. No es `status: "active"`, que es lo que
   * usan `promotions` y `upgrades` unas líneas más abajo — dos colecciones,
   * dos formas. Comprobado contra producción: los diez documentos de
   * `subscriptions` tienen `status` a `undefined`. Copiar el `where("status",
   * "==", "active")` de las promociones aquí no dejaría fuera a los retirados:
   * dejaría fuera a TODOS, y la ficha de pago se quedaría sin paquetes.
   *
   * Se compara con `!== false` y no con `=== true` a propósito: un documento sin
   * el campo se considera vendible. Un paquete nuevo al que se le olvide poner
   * la bandera debe salir en la lista y verse; desaparecer sin ruido es peor.
   */
  getSubscriptionsConfig(state: SubscriptionsState): Array<SubscriptionInfo> {
    return state.SubscriptionsConfiguration.filter(
      (item: any) => item[1]?.active !== false
    );
  },

  isVIP(state: SubscriptionsState, getters: any, rootState: any): boolean {
    // El nivel más alto (academia + CMMI). Se compara por familia: un nivel que
    // llegue con la etiqueta retirada cuenta igual, y uno que no se reconozca
    // NO cuenta — este getter enciende adornos, no debe encenderlos de más.
    return packageLabelsMatch(state.UserPackage, PKG_WIN);
  },
  getUserCurrentPackage(state: SubscriptionsState): SubscriptionPackage {
    return state.UserPackage;
  },
  /**
   * ¿SE SABE YA lo que el usuario tiene contratado?
   *
   * Quien decida qué se le ofrece a alguien tiene que preguntar esto ANTES de
   * mirar `getUserCurrentPackage`, porque hasta que este getter dice «listo» el
   * paquete vale `none` por defecto y no porque se haya comprobado.
   */
  getUserPackageStatus(state: SubscriptionsState): UserPackageStatus {
    return state.UserPackageStatus;
  },
  isUserActive(state: SubscriptionsState): boolean {
    return state.isUserActive;
  },
  isUserDistributor(state: SubscriptionsState): boolean {
    return state.isUserDistributor;
  },
  /**
   * La suscripción del usuario por doc-id.
   *
   * Si el id pedido pertenece a una FAMILIA con dos generaciones de nombre
   * comercial (`trading-academy`/`gtr-fly`, `gtr-lite`/`gtr-go`) se miran las
   * dos y gana la que caduca más tarde. Es el único punto por el que pasan
   * TODAS las tarjetas y paneles que preguntan "¿tiene academia?", así que
   * arreglarlo aquí los arregla a todos — y con un solo documento presente el
   * resultado es exactamente el de siempre.
   *
   * Las LICENCIAS son familias desde 2026-07-24, por lo mismo: quien pregunte
   * por `distributor-license` acepta además la GLOBAL
   * (`gtr-distributor-license`), y quien pregunte por
   * `lite-distributor-license` lo mismo. Ésa es la razón de tocar aquí y no en
   * cada tarjeta: por este getter pasan `ScholarshipCard`, `SubscriptionsCard`,
   * `SubscriptionsPanel`, `GtrBasic` y `GtrFly`, y todas preguntan por el id
   * viejo. El EMPAREJAMIENTO no se pierde: `distributor-license` NO trae la del
   * Lite ni al revés — sólo se añade la global a las dos familias.
   */
  getUserSubscription(state: SubscriptionsState, getters: any, rootState: any) {
    return (subscriptionId: string): UserSubscription => {
      const map = new Map(...[state.UserSubscriptions]);
      // El agrupamiento por familia vive en `packageIds`, no aquí: es el mismo
      // que usan las herramientas de administración, que leen un `Map` en vez
      // del estado del store, y con dos copias se desincronizan.
      const family = subscriptionFamilyOf(subscriptionId);

      const found = family
        .map((id) => map.get(id) as UserSubscription | undefined)
        .filter((s): s is UserSubscription => s != undefined)
        .sort((a, b) => endMs(b) - endMs(a));

      const subs =
        found[0] ||
        ({
          username: rootState.auth.userProfileData?._usernamePK,
          subscriptionId: subscriptionId,
          isFirstTime: true,
        } as UserSubscription);
      return subs;
    };
  },
  getSubscriptionConfig(state: SubscriptionsState) {
    return (subscriptionId: string): SubscriptionInfo | undefined => {
      return new Map(...[state.SubscriptionsConfiguration]).get(
        subscriptionId
      ) as SubscriptionInfo;
    };
  },
  durationUnitLocalized(state: SubscriptionsState) {
    return (duration: number, durationUnit: string): string => {
      let response = "";
      const lang = "es-MX";
      if (lang == "es-MX") {
        switch (durationUnit) {
          case "days":
          case "remainingDays":
            switch (duration as number) {
              case 1:
                response = "día";
                break;
              case 0:
              default:
                response = "días";
                break;
            }
            break;
          case "months":
            switch (duration as number) {
              case 1:
                response = "mes";
                break;
              case 0:
              default:
                response = "meses";
                break;
            }
            break;
          case "years":
            switch (duration as number) {
              case 1:
                response = "año";
                break;
              case 0:
              default:
                response = "años";
                break;
            }
            break;
        }
      }
      return response;
    };
  },
  getSubscriptionsPromotions(
    state: SubscriptionsState
  ): Array<SubscriptionPromotion> {
    return state.SubscriptionsPromotions.map((item) => {
      return item[1];
    });
  },
  getSubscriptionsUpgrades(
    state: SubscriptionsState
  ): Array<SubscriptionUpgrade> {
    return state.SubscriptionsUpgrades.map((item) => {
      return item[1];
    });
  },
  getSubscriptionsUpgrade(state: SubscriptionsState, getters: any) {
    return (upgradeId: string): SubscriptionUpgrade | undefined => {
      return new Map(...[state.SubscriptionsUpgrades]).get(
        upgradeId
      ) as SubscriptionUpgrade;
    };
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
